<template>
  <div class="subscription">
    <header class="header">
      <div class="container">
        <div class="header-content">
          <div class="logo-container">
            <img src="@/assets/logo.png" alt="Forms Pro Logo" class="logo-img" />
            <h1 class="logo">Forms Pro for Google Forms</h1>
          </div>
          <nav class="nav">
            <router-link to="/" class="nav-link">Home</router-link>
            <a href="/privacy" class="nav-link">Privacy</a>
            <a href="/terms" class="nav-link">Terms</a>
            <router-link to="/subscription" class="nav-link">Subscription</router-link>
          </nav>
        </div>
      </div>
    </header>

    <main class="main">
      <div class="container">
        <div class="content-wrapper">
          <h1 class="page-title">Subscription Agreement</h1>
          <p class="update-date"><strong>Last updated:</strong> August 17, 2026</p>

          <section class="section intro">
            <p>
              This Subscription Agreement (hereinafter referred to as the "Agreement") is entered into between you ("User") and Yikens Technology HK Limited ("we" or the "Company") regarding the subscription service for the Forms Pro for Google Forms application (hereinafter referred to as the "App").
            </p>
          </section>

          <section class="section">
            <h2>1. Subscription Service</h2>
            <h3>1.1 The App offers a Premium Membership subscription service, which provides access to all features, including but not limited to:</h3>
            <ul>
              <li>Create unlimited forms and surveys</li>
              <li>Advanced form templates and customization</li>
              <li>Real-time analytics and reporting</li>
              <li>Integration with Google Forms and other services</li>
              <li>Export data in multiple formats</li>
              <li>Custom branding and themes</li>
              <li>Priority customer support</li>
              <li>Ad-free experience</li>
            </ul>
          </section>

          <section class="section">
            <h2>2. Subscription Fees and Periods</h2>
            <h3>2.1 Available purchase options in the United States App Store include:</h3>
            <ul>
              <li><strong>Monthly Premium:</strong> $34.99/month</li>
              <li><strong>Annual Premium:</strong> $69.99/year</li>
              <li><strong>Lifetime Access:</strong> $79.99 one-time purchase</li>
            </ul>
            <h3>2.2 Prices and availability may vary by country or region. The price displayed by the App Store at the time of purchase is the final applicable price.</h3>
          </section>

          <section class="section">
            <h2>3. Payment and Renewal</h2>
            <h3>3.1 Monthly and annual subscriptions automatically renew through your App Store account unless auto-renewal is turned off at least 24 hours before the end of the current period.</h3>
            <h3>3.2 Renewal fees will be charged to your account within 24 hours prior to the end of the current period.</h3>
            <h3>3.3 Lifetime Access is a one-time purchase and does not automatically renew.</h3>
          </section>

          <section class="section">
            <h2>4. Free Trial</h2>
            <h3>4.1 We may offer a limited-time free trial period. After the trial period ends, the subscription will automatically convert to a paid subscription unless canceled in advance.</h3>
          </section>

          <section class="section">
            <h2>5. Cancellation and Refunds</h2>
            <h3>5.1 You can manage or cancel your subscription at any time through your App Store account settings.</h3>
            <h3>5.2 After cancellation, you will retain access to the subscription benefits for the remainder of the paid period.</h3>
            <h3>5.3 Purchases and refund requests are handled in accordance with Apple's App Store policies. Please contact Apple Support to request a refund where applicable.</h3>
          </section>

          <section class="section">
            <h2>6. Service Changes</h2>
            <h3>6.1 We reserve the right to modify subscription prices and terms at any time, with changes taking effect in the next subscription period.</h3>
          </section>

          <section class="section contact">
            <h2>7. Contact Us</h2>
            <p>For any subscription-related questions, please contact:</p>
            <p class="contact-info">
              Email: <a href="mailto:sl9283191@gmail.com" class="email-link">sl9283191@gmail.com</a>
            </p>
          </section>

          <section class="section important-note">
            <h2>Important Note</h2>
            <ul>
              <li>This Agreement, together with the Terms of Use and Privacy Policy, constitutes the complete agreement.</li>
              <li>Continued use constitutes your acceptance of all terms of this Subscription Agreement.</li>
            </ul>
          </section>
        </div>
      </div>
    </main>

    <footer class="footer">
      <div class="container">
        <div class="footer-bottom">
          <p>&copy; 2026 Forms Pro for Google Forms. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'Subscription'
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.subscription {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fc;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header */
.header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 1rem 0;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-img {
  height: 32px;
  width: auto;
  object-fit: contain;
}

.logo {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: 0;
  margin: 0;
}

.nav {
  display: flex;
  gap: 2rem;
}

.nav-link {
  color: #666;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  transition: color 0.3s;
  position: relative;
  padding-bottom: 2px;
  border-bottom: 2px solid transparent;
}

.nav-link:hover {
  color: #1a1a1a;
}

.nav-link.router-link-active {
  color: #4c51bf;
  border-bottom-color: #4c51bf;
}

/* Main Content */
.main {
  flex: 1;
  padding: 60px 0;
}

.content-wrapper {
  background: white;
  padding: 48px;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  max-width: 900px;
  margin: 0 auto;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 8px;
  text-align: center;
  letter-spacing: 0;
}

.update-date {
  text-align: center;
  color: #666;
  margin-bottom: 40px;
  font-weight: 600;
}

.section {
  margin-bottom: 40px;
}

.section.intro {
  background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
  padding: 24px;
  border-radius: 12px;
  border-left: 4px solid #4c51bf;
  margin-bottom: 48px;
}

.section.intro p {
  margin-bottom: 0;
}

.section.contact {
  background: #f8f9fc;
  padding: 24px;
  border-radius: 12px;
  text-align: center;
}

.section h2 {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e5e7eb;
}

.section h3 {
  font-size: 1.1rem;
  color: #4b5563;
  margin: 16px 0 12px;
  font-weight: 600;
  line-height: 1.6;
}

.section p {
  line-height: 1.8;
  color: #4b5563;
  margin-bottom: 16px;
  font-size: 1rem;
}

.section p:last-child {
  margin-bottom: 0;
}

.section ul {
  margin: 16px 0 16px 24px;
  list-style-type: disc;
}

.section li {
  line-height: 1.8;
  color: #4b5563;
  margin-bottom: 8px;
  padding-left: 8px;
}

.section strong {
  color: #1a1a1a;
  font-weight: 600;
}

.contact-info {
  font-size: 1.1rem;
  padding: 16px;
  background: white;
  border-radius: 8px;
  margin-top: 16px;
}

.email-link {
  color: #4c51bf;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s;
  border-bottom: 2px solid transparent;
}

.email-link:hover {
  color: #5b21b6;
  border-bottom-color: #5b21b6;
}

.important-note {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  padding: 24px;
  border-radius: 12px;
  border-left: 4px solid #f59e0b;
}

.important-note h2 {
  color: #d97706;
  border-bottom-color: #f59e0b;
}

.important-note ul {
  list-style-type: none;
  margin-left: 0;
}

.important-note li {
  position: relative;
  padding-left: 2rem;
  margin-bottom: 12px;
  color: #78350f;
  font-weight: 500;
}

.important-note li:before {
  content: '⚠️';
  position: absolute;
  left: 0;
  font-size: 1.25rem;
}

/* Footer */
.footer {
  background: #1a1a1a;
  color: #999;
  padding: 32px 0;
}

.footer-bottom {
  text-align: center;
  font-size: 0.9rem;
}

/* Responsive */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 1rem;
  }

  .nav {
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;
  }

  .content-wrapper {
    padding: 32px 24px;
  }

  .page-title {
    font-size: 2rem;
  }

  .section h2 {
    font-size: 1.5rem;
  }

  .section h3 {
    font-size: 1rem;
  }

  .logo {
    font-size: 1.25rem;
  }
  
  .logo-img {
    height: 28px;
  }
  
  .logo-container {
    gap: 8px;
  }
}
</style>
